import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import useToast from "../../../hooks/useToast";
import { TOAST } from "../../../constants/ToastMessages";
import { isEmpty } from "lodash";
import useFetch from "../../../hooks/useFetch";
import usePostRequest from "../../../hooks/usePostRequest";
import useApiCall from "../../../hooks/useApiCall";
import useTableSorting from "../../../hooks/useTableSorting";
import {
  convertUnderscoreToSpaces,
  formatDate,
  getStringParamsFromObject,
  exportToCSV,
  getStatusChipProps,
} from "../../../utils";
import SearchInput from "../../../components/SearchInput";
import OptionMenu from "../../../components/OptionMenu";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ChunksSidebar from "./ChunksSidebar";
import { INDIGO, SLATE } from "../../../constants/Colors";
import {
  WEBSITE_FILTER_OPTIONS,
  SITEMAP_REFRESH_REVIEW_STATUS,
} from "../../../constants/Constants";
import { URL_COL_TEMPLATE, URL_COLUMN_HEADERS } from "./urlSidebarConstants";
import useUrlBulkActions from "../../../hooks/useUrlBulkActions";
import ExportConfirmDialog from "./ExportConfirmDialog";
import UrlTableRow from "./UrlTableRow";

export default function UnifiedUrlsSidebar({
  currentApp,
  tokens,
  isOpen,
  onClose,
  selectedStatus = "all",
  website = null,
  readOnly = false,
}) {
  const { success, error } = useToast();
  const [urls, setUrls] = useState({});
  const [selectedChunkUrl, setSelectedChunkUrl] = useState(null);
  const [isChunksSidebarOpen, setIsChunksSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const [statusFilters, setStatusFilters] = useState(
    WEBSITE_FILTER_OPTIONS.map((el) => ({
      title: convertUnderscoreToSpaces(el),
      value: el,
      selected: selectedStatus === "all" ? false : el === selectedStatus,
      dotColor: getStatusChipProps(el).sx?.color,
      onClick: () => {
        setStatusFilters((prev) => {
          const next = [...prev];
          const i = next.findIndex((f) => f.value === el);
          if (i !== -1) next[i] = { ...next[i], selected: !next[i].selected };
          return next;
        });
      },
    }))
  );

  const [sitemapReviewFilters, setSitemapReviewFilters] = useState(
    SITEMAP_REFRESH_REVIEW_STATUS.map((el) => ({
      title: convertUnderscoreToSpaces(el),
      value: el,
      selected: false,
      onClick: () => {
        setSitemapReviewFilters((prev) => {
          const next = [...prev];
          const i = next.findIndex((f) => f.value === el);
          if (i !== -1) next[i] = { ...next[i], selected: !next[i].selected };
          return next;
        });
      },
    }))
  );

  const { paginationParams, setPaginationParams, onChangeRowsPerPage, onChangePage } =
    useTableSorting({ page_size: 25, page: 0, order_by: "url", order: "asc" }, null);

  const exportCallMade = useRef(false);
  const refreshDaysRef = useRef(null);
  const tagRequestSeq = useRef({});

  useEffect(() => {
    setPaginationParams((prev) => ({ ...prev, page: 0 }));
  }, [selectedStatus]); // eslint-disable-line

  useEffect(() => {
    if (isOpen) setPaginationParams((prev) => ({ ...prev, page: 0 }));
  }, [isOpen]); // eslint-disable-line

  const buildApiUrl = (pageSize) => {
    const base = website
      ? `/v1/apps/${currentApp.id}/websites/${website.id}/urls`
      : `/v1/apps/${currentApp.id}/urls`;
    const p = pageSize != null ? { ...paginationParams, page_size: pageSize } : paginationParams;
    let url = `${base}?${getStringParamsFromObject(p)}&search=${search.trim()}`;
    const statusStr = statusFilters.filter((s) => s.selected).map((s) => s.value).join(",");
    const sitemapStr = sitemapReviewFilters.filter((s) => s.selected).map((s) => s.value).join(",");
    if (!isEmpty(statusStr)) url += "&status=" + statusStr;
    else if (selectedStatus !== "all") url += `&status=${selectedStatus}`;
    if (!isEmpty(sitemapStr)) url += "&sitemap_refresh_review_status=" + sitemapStr;
    return url;
  };

  const { data, loading } = useFetch(
    buildApiUrl(),
    tokens,
    [paginationParams.page, paginationParams.page_size, search, selectedStatus, currentApp?.id, website?.id, refreshTrigger, statusFilters, sitemapReviewFilters],
    {}
  );

  const { data: exportData, fetchData: fetchExportData } = useFetch(
    buildApiUrl(10000),
    tokens,
    [],
    [],
    false
  );

  const { sendPostRequest: sendUpdateStatusRequest } = usePostRequest(
    website
      ? `/v1/apps/${currentApp.id}/websites/${website.id}/urls/update_status`
      : `/v1/apps/${currentApp.id}/urls/update_status`,
    tokens
  );

  const { makeRequest } = useApiCall();

  const updateUrl = (url, body, { ignoreError = false } = {}) =>
    makeRequest(`/v1/apps/${currentApp.id}/websites/${url.source_id}/urls/${url.id}`, {
      method: "PUT",
      authTokens: tokens,
      body,
      ignore_error: ignoreError,
    });

  useEffect(() => {
    if (data) {
      const actualCount = data.count || data.filtered_count || data.data?.length || 0;
      setUrls({
        data: data.data || data.urls || [],
        total: data.total_count || data.total || 0,
        count: actualCount,
        default_refresh_day: data.default_refresh_day || 30,
        ...data,
      });
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    setPaginationParams((prev) => ({ ...prev, page: 0 }));
  }, [search, statusFilters, sitemapReviewFilters]); // eslint-disable-line

  const bulkUpdateUrls = async (status, extra = {}) => {
    try {
      const response = await sendUpdateStatusRequest({ status, url_ids: selectedUrls, ...extra });
      if (response) {
        await new Promise((r) => setTimeout(r, 500));
        setRefreshTrigger((prev) => prev + 1);
        setSelectedUrls([]);
      }
    } catch (err) {
      console.error("Error updating URLs:", err);
      error(TOAST.websites.statusUpdateError(status));
    }
  };

  const handleExportClick = () => setExportDialogOpen(true);

  const handleExportConfirm = () => {
    setExportDialogOpen(false);
    if (exportCallMade.current) return;
    exportCallMade.current = true;
    setIsExporting(true);
    fetchExportData(buildApiUrl(10000), tokens);
  };

  useEffect(() => {
    if (!isExporting || !exportCallMade.current) return;
    const allData = exportData?.data || exportData?.urls;
    if (!allData) return;
    try {
      const tz = new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2];
      const csvData = allData.map((u) => ({
        url: u.url, title: u.title || "",
        status: convertUnderscoreToSpaces(u.status),
        sitemap_refresh_review_status: convertUnderscoreToSpaces(u.sitemap_refresh_review_status),
        metadata: u.metadata ? JSON.stringify(u.metadata) : "",
        tags: (u.tags || []).join(", "),
        indexed_at: u.indexed_at ? formatDate(u.indexed_at) : "",
        created_at: formatDate(u.created_at),
        updated_at: formatDate(u.updated_at),
        ...(u.website_url && { website_url: u.website_url }),
      }));
      const headers = ["url", "title", "status", "sitemap_refresh_review_status", "metadata", "tags",
        `indexed_at (${tz})`, `created_at (${tz})`, `updated_at (${tz})`,
        ...(website ? [] : ["website_url"])];
      const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const suffix = website ? website.url.replace(/[^a-zA-Z0-9]/g, "_") : `${selectedStatus}_urls`;
      exportToCSV(csvData, `urls_export_${suffix}_${ts}.csv`, headers);
      success(TOAST.websites.exported);
    } catch (err) {
      error(TOAST.websites.exportError);
    } finally {
      setIsExporting(false);
      exportCallMade.current = false;
    }
  }, [exportData]); // eslint-disable-line

  // website_tags arrives from a jsonb column aliased in a raw select. Rails
  // normally maps that back to an array, but accept a JSON string too rather
  // than render "[\"Production\"]" as a single chip if it ever does not.
  const inheritedTagsFor = (url) => {
    const raw = url.website_tags;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleRefreshDaysChange = (url, e) => {
    const days = parseInt(e.target.value);
    if (days <= 0 || days > 365) { error(TOAST.websites.refreshDaysError); return; }
    // useApiCall already surfaces the error via toast; swallow to avoid an unhandled rejection.
    updateUrl(url, { url: { refresh_days: days } }).catch(() => {});
  };

  const handleTagsChange = async (url, newTags) => {
    const previous = url.tags || [];
    const seq = (tagRequestSeq.current[url.id] || 0) + 1;
    tagRequestSeq.current[url.id] = seq;

    setUrls((prev) => ({
      ...prev,
      data: prev.data.map((u) => (u.id === url.id ? { ...u, tags: newTags } : u)),
    }));

    try {
      await updateUrl(url, { url: { tags: newTags } }, { ignoreError: true });
    } catch (err) {
      // A later edit to this row superseded this request — its own result wins.
      if (tagRequestSeq.current[url.id] !== seq) return;

      setUrls((prev) => ({
        ...prev,
        data: prev.data.map((u) => (u.id === url.id ? { ...u, tags: previous } : u)),
      }));
      error(TOAST.websites.tagsUpdateError);
    }
  };

  const activeFilterCount =
    statusFilters.filter((f) => f.selected).length +
    sitemapReviewFilters.filter((f) => f.selected).length;

  const urlList = urls?.data || [];
  const { selectedUrls, setSelectedUrls, handleCheckboxClick, handleSelectAllClick, allSelected, someSelected } = useUrlBulkActions(urlList);

  const title = website
    ? website.url
    : selectedStatus === "all"
    ? "All URLs"
    : `${convertUnderscoreToSpaces(selectedStatus)} URLs`;

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 21474834 }}
        PaperProps={{
          sx: {
            width: "70%",
            minWidth: 640,
            maxWidth: 1100,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: SLATE[50],
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: "#ffffff", borderBottom: `1px solid ${SLATE[200]}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.08em", mb: "8px" }}>
              Knowledge Base — URLs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <LinkIcon sx={{ color: INDIGO[500], fontSize: "1.125rem", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: SLATE[900], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {title}
              </Typography>
              {urls?.total != null && (
                <Box sx={{ flexShrink: 0, px: "10px", py: "2px", bgcolor: SLATE[100], borderRadius: "20px" }}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[600] }}>
                    {urls.total.toLocaleString()} URLs
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] }, mt: "2px" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Toolbar */}
        <Box sx={{ px: 3, py: 1.5, bgcolor: "#ffffff", borderBottom: `1px solid ${SLATE[200]}`, display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchInput value={search} onChange={(v) => setSearch(v)} placeholder="Search URLs..." />
          </Box>

          <OptionMenu
            enableCheckBoxes
            zIndex={21474836}
            options={[
              { title: "Status", isHeader: true, disabled: true },
              ...statusFilters,
              { title: "Sitemap Review Status", isHeader: true, disabled: true },
              ...sitemapReviewFilters,
            ]}
            customStyle={{ width: "40ch" }}
            button={
              <Button variant="outlined" size="small" sx={{ borderColor: activeFilterCount > 0 ? INDIGO[300] : SLATE[300], color: activeFilterCount > 0 ? INDIGO[600] : SLATE[600], bgcolor: activeFilterCount > 0 ? INDIGO[50] : "transparent", textTransform: "none", gap: 0.75, "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50] } }}>
                <FilterIcon sx={{ fontSize: "1rem" }} />
                Filters
                {activeFilterCount > 0 && (
                  <Box sx={{ bgcolor: INDIGO[600], color: "#fff", borderRadius: "10px", px: "6px", fontSize: "0.6875rem", fontWeight: 700, lineHeight: "18px" }}>
                    {activeFilterCount}
                  </Box>
                )}
              </Button>
            }
          />

          {!readOnly && (
            <OptionMenu
              options={[
                { title: "Index", onClick: () => bulkUpdateUrls("to_be_indexed"), value: "index" },
                { title: "Force Index", onClick: () => bulkUpdateUrls("to_be_indexed", { force_index: true }), value: "force_index" },
                { title: "Skip", onClick: () => bulkUpdateUrls("to_be_skipped"), value: "skip" },
                { title: "Delete", onClick: () => bulkUpdateUrls("to_be_deleted"), value: "delete" },
              ]}
              button={
                <Button variant="outlined" size="small" disabled={selectedUrls.length === 0} sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50], color: INDIGO[600] }, "&.Mui-disabled": { borderColor: SLATE[200], color: SLATE[400] } }}>
                  Bulk Actions
                  {selectedUrls.length > 0 && (
                    <Box sx={{ bgcolor: INDIGO[600], color: "#fff", borderRadius: "10px", px: "6px", fontSize: "0.6875rem", fontWeight: 700, lineHeight: "18px", ml: 0.75 }}>
                      {selectedUrls.length}
                    </Box>
                  )}
                </Button>
              }
              zIndex={21474836}
            />
          )}

          <Tooltip
            title={
              activeFilterCount > 0 || selectedStatus !== "all" || search.trim()
                ? `Exports ${urls.count?.toLocaleString() ?? "…"} filtered URLs (up to 10,000) — filtered from ${urls.total?.toLocaleString() ?? "…"} total`
                : `Exports all ${urls.total?.toLocaleString() ?? "…"} URLs (up to 10,000)`
            }
            placement="bottom-end"
            PopperProps={{ sx: { zIndex: 21474837 } }}
          >
            <span>
              <Button variant="contained" size="small" onClick={handleExportClick} disabled={isExporting || urlList.length === 0} sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", gap: 0.75, "&:hover": { boxShadow: "none" }, "&.Mui-disabled": { bgcolor: SLATE[200], color: SLATE[400] } }}>
                {isExporting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon sx={{ fontSize: "1rem" }} />}
                {isExporting ? "Exporting…" : "Export CSV"}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Filtered count */}
        {search.trim() !== "" && !loading && urls?.count != null && (
          <Box sx={{ px: 3, py: 1, bgcolor: INDIGO[50], borderBottom: `1px solid ${INDIGO[100]}` }}>
            <Typography sx={{ fontSize: "0.8125rem", color: INDIGO[700] }}>
              Showing {urls.count} filtered {urls.count === 1 ? "URL" : "URLs"}
            </Typography>
          </Box>
        )}

        {/* Table */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Box sx={{ bgcolor: "#ffffff", minWidth: 820 }}>
            {/* Column headers */}
            <Box sx={{ display: "grid", gridTemplateColumns: URL_COL_TEMPLATE, px: 3, py: 1, borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50], position: "sticky", top: 0, zIndex: 1, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {!readOnly && (
                  <Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={handleSelectAllClick} sx={{ p: 0, color: SLATE[400], "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: INDIGO[500] } }} />
                )}
              </Box>
              {URL_COLUMN_HEADERS.map((col) => (
                <Typography key={col} sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {col}
                </Typography>
              ))}
            </Box>

            {loading ? (
              <LoadingSpinner message="Loading URLs..." subMessage="Fetching URLs" />
            ) : urlList.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 10 }}>
                <Typography sx={{ fontSize: "1.5rem", mb: 1 }}>🔗</Typography>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900], mb: "4px" }}>No URLs found</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                  {search ? "Try adjusting your search or filters." : "No URLs available."}
                </Typography>
              </Box>
            ) : (
              urlList.map((url, idx) => (
                <UrlTableRow
                  key={url.id}
                  url={url}
                  isChecked={selectedUrls.includes(url.id)}
                  onCheckboxChange={handleCheckboxClick}
                  onRefreshDaysChange={handleRefreshDaysChange}
                  refreshDaysRef={refreshDaysRef}
                  defaultRefreshDay={urls.default_refresh_day}
                  onViewChunks={(u) => { setSelectedChunkUrl(u); setIsChunksSidebarOpen(true); }}
                  idx={idx}
                  totalCount={urlList.length}
                  showSourceDomain={!website}
                  inheritedTags={inheritedTagsFor(url)}
                  onTagsChange={handleTagsChange}
                  readOnly={readOnly}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Pagination */}
        {!loading && urlList.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${SLATE[200]}`, bgcolor: "#ffffff", flexShrink: 0 }}>
            <TablePagination component="div" count={urls.count || 0} page={paginationParams.page} rowsPerPage={paginationParams.page_size} rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => onChangePage(p)} onRowsPerPageChange={(e) => onChangeRowsPerPage(parseInt(e.target.value, 10))}
              sx={{ "& .MuiTablePagination-toolbar": { minHeight: 48 }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8125rem", color: SLATE[600] } }}
            />
          </Box>
        )}
      </Drawer>

      <ExportConfirmDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} onConfirm={handleExportConfirm} count={urls?.count} total={urls?.total} />

      {isChunksSidebarOpen && selectedChunkUrl && (
        <ChunksSidebar
          website={website || { id: selectedChunkUrl.source_id }}
          url={selectedChunkUrl}
          currentApp={currentApp}
          tokens={tokens}
          isOpen={isChunksSidebarOpen}
          onClose={() => { setIsChunksSidebarOpen(false); setSelectedChunkUrl(null); }}
        />
      )}
    </>
  );
}
